<template>
  <DsPageContent
    class="ds-display-flex ds-flex-direction-column"
    data-allow-mismatch
  >
    <DsSection
      :with-space-top="false"
      class="ds-padding-top-48"
    >
      <div
        v-if="!pageData"
        style="width: 100%; height: 50dvh; place-content: center"
      >
        <Loading />
      </div>
      <div
        v-else
        class="ds-grid"
      >
        <div
          v-if="socialItems.length"
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--1"
        >
          <DsSocialBar
            class="page-social-bar"
            disposition="vertical"
            :items="socialItems"
          />
        </div>
        <div class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8 ds-lg-grid__col--offset-1">
          <DsLinkBack
            v-if="pageData.breadcrumb"
            class="ds-md-margin-bottom-48"
            :class="!!content.disclaimer ? 'ds-margin-bottom-48' : 'ds-margin-bottom-36'"
            :text="pageData.breadcrumb.title"
            :url="pageData.breadcrumb.url"
          />
          <RichText
            v-if="content.disclaimer"
            class="ds-margin-bottom-24 ds-md-margin-bottom-48"
            :data="content.disclaimer"
          />
          <DsEyebrow :text="content.locationAndTime" />
          <h1
            v-if="content.title"
            class="ds-heading ds-md-heading-large ds-margin-bottom-24"
          >
            {{ content.title }}
          </h1>
          <RichText
            v-if="content.introText"
            class="ds-text-medium"
            :data="content.introText"
          />
          <div
            v-if="content.image"
            class="ds-margin-top-24 ds-md-margin-y-48"
          >
            <DsImageElement :image="content.image" />
            <div
              v-if="content.downloadableImage"
              class="ds-display-flex ds-flex-direction-column ds-md-flex-direction-row ds-md-justify-content-space-between ds-md-align-items-flex-end ds-md-margin-top-24"
            >
              <p class="ds-text-small ds-margin-top-12 ds-md-margin-top-0">
                {{ labels.downloadImageLegend }}
              </p>
              <DsLink
                class="ds-margin-top-12 ds-md-margin-top-0"
                v-bind="content.downloadImageLink"
              />
            </div>
          </div>
          <RichText
            v-if="content.text"
            class="ds-margin-top-24 ds-md-margin-top-48"
            :data="content.text"
            :content-locale="pageData.locale"
          />
        </div>
      </div>
    </DsSection>
  </DsPageContent>
</template>
<script setup>
import { DsEyebrow, DsImageElement, DsLink, DsLinkBack, DsPageContent, DsSection, DsSocialBar } from 'awds'
import Loading from '../../../core/components/Loading.vue'
import { queryPagePressAndMediaDetail } from '../../data-layer/queries'

const { queryContentful } = useQueries()
const route = useRoute()
const { locale: localeRef } = useI18n()
const { getLocaleIso } = useLocales()
const slug = route.params.slug
const { dataPageMapper } = useDataMapper()
const { mappers } = usePressAndMediaDataLayer()
const { onBeforeAdyenData, onAfterAdyenData, pageData, content } = usePressAndMediaPageData()
const locale = getLocaleIso(localeRef.value)
const { labels } = usePressAndMediaGlobalData()
const { isScreenLarge } = useScreen()

const cacheKey = 'press-and-media-' + localeRef.value + '-' + slug

// Route Guard to prevent Press and Media Detail to render before IR Publication date (For sensitive IR Releases only)
// NOTE: Keep it commented out because is just needed during IR Publication day
// definePageMeta({
//   middleware: defineNuxtRouteMiddleware(async (to) => {
//     const slug = to.params?.slug
//     console.log('[Press Detail Middleware]', slug)
//     if (slug?.includes('h2-2024')) {
//       console.log('[Press Detail Middleware] VALIDATE CONTENT ENABLED')
//       const contentUpdateResult = await $fetch('/api/v3/content-enabled')
//       console.log('[Press Detail Middleware] CONTENT ENABLED RESULT', contentUpdateResult)
//       if (contentUpdateResult) {
//         console.log('[Press Detail Middleware] CONTENT ENABLED')
//         return true
//       }
//       return navigateTo('/press-and-media', { replace: true })
//     }
//     console.log('[Press Detail Middleware] CONTINUE')
//     return true
//   })
// })

await onBeforeAdyenData({ withRoutes: true, globalDataContentType: 'PagePressAndMedia' })

const { data } = await useAsyncData(cacheKey, async () => {
  const pressAndMediaDetailData = await queryContentful(
    {
      query: queryPagePressAndMediaDetail,
      variables: { slug, locale, preview: useRuntimeConfig().public.preview }
    },
    { log: false }
  )

  if (
    !pressAndMediaDetailData ||
    !Object.keys(pressAndMediaDetailData).length ||
    !pressAndMediaDetailData?.page?.items?.[0]
  ) {
    return null
  }

  const entry = pressAndMediaDetailData.page?.items[0]
  const entryParent = pressAndMediaDetailData?.pageParent ? pressAndMediaDetailData.pageParent?.items[0] : null

  const pageData = dataPageMapper(
    {
      ...entry,
      ...{ parent: entryParent?.newsroomLink || null }
    },
    mappers
  )

  // Modify seo ogImage
  if (pageData.seo) {
    const ogImage = pageData.seo?.ogImage || pageData.content?.image?.large?.src || null
    pageData.seo.ogImage = ogImage
  }

  return {
    pageData
  }
})

const socialItems = computed(() => {
  if (!pageData.value?.seo) {
    return []
  }
  const url = route.fullPath
  const title = pageData.value.seo.title
  const description = pageData.value.seo.description

  const networks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&title=${title}&description=${description}`,
    twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
  }

  return isScreenLarge.value
    ? [
        {
          iconName: 'Facebook',
          text: `Facebook ${labels.value.opensInNewWindow}`,
          url: networks.facebook
        },
        {
          iconName: 'Linkedin',
          text: `Linkedin ${labels.value.opensInNewWindow}`,
          url: networks.linkedin
        },
        {
          iconName: 'Twitter',
          text: `Twitter ${labels.value.opensInNewWindow}`,
          url: networks.twitter
        }
      ]
    : []
})

// watchEffect(() => {
//   iteration.value += 1
//   // pageData.value = data.value
//   // onAfterAdyenData(data.value)
// })

await onAfterAdyenData(data.value)
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';

.page-social-bar {
  float: left; // removes unwanted space when position sticky has top property
  margin-bottom: $spacing-48;
  position: sticky;
  top: calc(50vh - 87px);
}
</style>
